/**
 * The attention card's check set, and the arithmetic its header and footer state.
 *
 * `components.md` → Attention card: *"The check set is six, and it is the same six as the
 * precedence order … Every attention item is exactly one of these six failing, which is
 * what makes the arithmetic in the header and footer add up in every state and the ceiling
 * real."*
 *
 * ## It passes three of the six, and that is a ruling rather than a shortcut
 *
 * The ruled set is Domain live · Prefix verified · No recalls active · Billing up to date ·
 * No imports waiting · Usage within plan. **Three of those still have no subject**: no
 * entitlement gate, no import pipeline, and §4 forbids the dashboard reading `scan_events`.
 * **Domain live gained its subject at U16** and joined the list, which is this section's
 * own prediction happening — the 2026-08-11 ruling said *two of six because no subject
 * exists*, and one now does. Rendering them would put four green ticks
 * on the screen on the strength of nothing having been checked — a dashboard telling a
 * brand their billing is fine when nothing has ever looked. `acceptance-tests.md`'s
 * governing line is *"nothing invented; open questions flagged rather than filled in."*
 *
 * U22 measured the same defect one level down: `domain live` ticked for an organisation with no
 * domain. From U25 {@link applicableChecks} drops a check per organisation on the same reasoning.
 *
 * So **the component is built to the full spec and the data is built to the phase**: the
 * card takes its check set as input, and every number it prints is derived from the set it
 * was given. Cameron ruled this on 2026-08-11, choosing it over rendering the mock's six.
 *
 * The consequence worth stating: as later phases give each check a subject, a check joins
 * this list and every count, sentence and ceiling follows it. Nothing about the card needed
 * touching when the third arrived — only `PHASE_2_CHECKS` and the tests that quote its
 * size — and there is no moment where a "temporary" two becomes a forgotten six.
 */

/** One thing the dashboard can say it has looked at. */
export interface Check {
  /** Stable across renders; the React key and the test's handle. */
  readonly id: string;
  /** How it reads when it is clear — a state, not an instruction. */
  readonly clear: string;
  /**
   * The attention kinds that mean this check is failing.
   *
   * The link is declared here rather than inferred so the arithmetic cannot drift from
   * the stack: an item whose kind matches no check would be counted in the header and
   * missing from the footer's list of what is still clear.
   */
  readonly failsOn: readonly string[];
  /**
   * The subject the gate reads. With none of it, the check is absent rather than clear —
   * a tick for a domain an organisation never added is a claim nothing checked (§3b).
   */
  readonly reads?: keyof CheckSubjects;
}

/** How much of each gate's subject an organisation holds. */
export interface CheckSubjects {
  readonly domains: number;
  /** Counter rows for the current window — none before the first rollup (U59). */
  readonly usage: number;
}

/**
 * Phase 2's subject-backed subset, in the ruled precedence order.
 *
 * Recall before prefix, which is the head of `components.md`'s order — recall → domain
 * error / disputed prefix → billing → everything else.
 */
export const PHASE_2_CHECKS: readonly Check[] = [
  { id: "recalls", clear: "no recalls active", failsOn: ["recall"] },
  { id: "domain", clear: "domain live", failsOn: ["domain-failing"], reads: "domains" },
  // Mirrors `trustRefusal` (`apps/api/src/ops/products.ts`): every state it refuses has a kind
  // here, and only `permitsPublishing()` clears it — Decision 2026-09-23 §3b.
  {
    id: "prefix",
    clear: "prefix verified",
    failsOn: ["prefix-missing", "prefix-unverified", "prefix-pending", "prefix-disputed"],
  },
  // No `reads`: every organisation holds a billing state. Churned is clear, because nothing
  // is owed — Drift Log §U60. A trial's last three days fail it: the plan is about to change.
  {
    id: "billing",
    clear: "billing in order",
    failsOn: ["billing-grace", "billing-degraded", "trial-closing"],
  },
  // Last, `components.md`'s order. Absent until a rollup has counted this window.
  {
    id: "usage",
    clear: "within plan limits",
    failsOn: ["usage-near-limit", "usage-over-limit"],
    reads: "usage",
  },
];

/**
 * A check mirrors one gate, fails exactly when that gate would refuse, and is absent when
 * that gate has nothing to read — Decision 2026-09-23 §3b.
 */
export function applicableChecks(
  subjects: CheckSubjects,
  checks: readonly Check[] = PHASE_2_CHECKS,
): readonly Check[] {
  return checks.filter((check) => check.reads === undefined || subjects[check.reads] > 0);
}

export interface CheckSummary {
  /** The header's sentence. Always a sentence, never a section label. */
  readonly title: string;
  /** The header's right-hand meta. */
  readonly meta: string;
  /** The footer's line. */
  readonly footer: string;
  readonly total: number;
  readonly clear: number;
}

/** English, and it has to be right at one — "1 thing", "1 check", "is" not "are". */
function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

/**
 * Everything the card's chrome says, derived from the set and the failures.
 *
 * Kept as one function returning all four strings because they are one statement split
 * across the card: "3 things need you today", "6 checks · 3 clear" and "the other 3 checks
 * are clear: …" are the same arithmetic three times, and computing them apart is how they
 * come to disagree.
 */
export function summariseChecks(
  checks: readonly Check[],
  failingKinds: readonly string[],
  /** Kinds whose items are `neutral` — failing, with nothing on the reader's desk. */
  quietKinds: readonly string[] = [],
): CheckSummary {
  const failing = checks.filter((check) =>
    check.failsOn.some((kind) => failingKinds.includes(kind)),
  );
  const loud = failing.filter((check) =>
    check.failsOn.some(
      (kind) => failingKinds.includes(kind) && !quietKinds.includes(kind),
    ),
  );
  const clear = checks.filter((check) => !failing.includes(check));
  const total = checks.length;

  if (failing.length === 0) {
    return {
      title: "Nothing needs you today",
      meta: `${String(total)} ${plural(total, "check", "checks")}, all clear`,
      // Truncated deliberately. The mock continues "and we'll email whoever owns it",
      // which describes a notification path no phase has built. c29's rule: do not name
      // a remedy that does not exist. The clause is owed when email lands.
      footer: "If any of these stops being true you'll find it here first.",
      total,
      clear: clear.length,
    };
  }

  const count = failing.length;
  return {
    // Same count, other verb when every failing check is quiet: "needs you" above a row
    // saying nothing is needed was a false sentence — U25, Cameron's ruling 2.
    title:
      loud.length === 0
        ? `${String(count)} ${plural(count, "thing is", "things are")} waiting on us, not you`
        : `${String(count)} ${plural(count, "thing needs", "things need")} you today`,
    meta: `${String(total)} ${plural(total, "check", "checks")} · ${String(clear.length)} clear`,
    footer:
      clear.length === 0
        ? "Every check is reporting something."
        : `The other ${String(clear.length)} ${plural(clear.length, "check is", "checks are")} clear: ${clear
            .map((check) => check.clear)
            .join(", ")}.`,
    total,
    clear: clear.length,
  };
}
